import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { profilePermissionGroups } from "../../utils/profilePermissions";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  colorIcon: {
    color: "#0035a2",
  },
  permissionsGrid: {
    marginTop: theme.spacing(1),
  },
  permissionGroup: {
    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
    paddingTop: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  permissionGroupTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.25),
  },
  permissionGroupActions: {
    paddingLeft: theme.spacing(1.5),
  },
  permissionItem: {
    minHeight: 28,
    marginTop: -4,
    marginBottom: -4,
    "& .MuiFormControlLabel-label": {
      fontSize: "0.875rem",
    },
  },
}));

const initialProfile = {
  name: "",
  permissions: [],
};

const Profiles = () => {
  const classes = useStyles();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [deletingProfile, setDeletingProfile] = useState(null);
  const [profileForm, setProfileForm] = useState(initialProfile);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/profiles");
      setProfiles(data);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadProfiles();
  }, []);

  const handleOpenModal = profile => {
    setSelectedProfile(profile || null);
    setProfileForm(profile ? { ...profile } : initialProfile);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProfile(null);
    setProfileForm(initialProfile);
  };

  const handleTogglePermission = permission => {
    setProfileForm(prevState => {
      const permissions = prevState.permissions || [];
      const nextPermissions = permissions.includes(permission)
        ? permissions.filter(item => item !== permission)
        : [...permissions, permission];

      return { ...prevState, permissions: nextPermissions };
    });
  };

  const handleSaveProfile = async () => {
    try {
      if (selectedProfile) {
        await api.put(`/profiles/${selectedProfile.id}`, profileForm);
      } else {
        await api.post("/profiles", profileForm);
      }
      toast.success("Perfil salvo com sucesso.");
      handleCloseModal();
      loadProfiles();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteProfile = async profileId => {
    try {
      await api.delete(`/profiles/${profileId}`);
      toast.success("Perfil excluído com sucesso.");
      setDeletingProfile(null);
      loadProfiles();
    } catch (err) {
      toastError(err);
    }
  };

  const isDefaultProfile = profile =>
    profile?.key === "admin" || profile?.key === "user";

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingProfile && `Excluir perfil ${deletingProfile.name}?`}
        open={Boolean(deletingProfile)}
        onClose={() => setDeletingProfile(null)}
        onConfirm={() => handleDeleteProfile(deletingProfile.id)}
      >
        Usuários vinculados a este perfil impedem a exclusão.
      </ConfirmationModal>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>{selectedProfile ? "Editar perfil" : "Novo perfil"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nome"
            value={profileForm.name}
            onChange={event => {
              const { value } = event.target;

              setProfileForm(prevState => ({
                ...prevState,
                name: value,
              }));
            }}
            variant="outlined"
            margin="dense"
            fullWidth
          />

          <Typography variant="subtitle2" color="textSecondary">
            Permissões por domínio e funcionalidade
          </Typography>
          <div className={classes.permissionsGrid}>
            {profilePermissionGroups.map(group => (
              <div className={classes.permissionGroup} key={group.label}>
                <Typography className={classes.permissionGroupTitle} variant="body2">
                  {group.label}
                </Typography>
                {group.root && (
                  <FormControlLabel
                    className={classes.permissionItem}
                    control={
                      <Checkbox
                        color="primary"
                        size="small"
                        checked={profileForm.permissions?.includes(group.root.key)}
                        onChange={() => handleTogglePermission(group.root.key)}
                      />
                    }
                    label={group.root.label}
                  />
                )}
                <Grid container spacing={1} className={classes.permissionGroupActions}>
                  {group.permissions.map(permission => (
                    <Grid item xs={12} sm={6} key={permission.key}>
                      <FormControlLabel
                        className={classes.permissionItem}
                        control={
                          <Checkbox
                            color="primary"
                            size="small"
                            checked={profileForm.permissions?.includes(permission.key)}
                            onChange={() => handleTogglePermission(permission.key)}
                          />
                        }
                        label={permission.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={handleSaveProfile} color="primary" variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <MainHeader>
        <Title description="Defina níveis de acesso e permissões da plataforma.">
          Perfis
        </Title>
        <MainHeaderButtonsWrapper>
          <IconButton
            onClick={() => handleOpenModal(null)}
            className={classes.colorIcon}
            size="large"
          >
            <AddIcon />
          </IconButton>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Identificador</TableCell>
              <TableCell align="center">Acessos</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profiles.map(profile => (
              <TableRow key={profile.id}>
                <TableCell>{profile.name}</TableCell>
                <TableCell>{profile.key}</TableCell>
                <TableCell align="center">
                  {profile.permissions?.filter(item => item.startsWith("page:")).length || 0}
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleOpenModal(profile)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={isDefaultProfile(profile)}
                    onClick={() => setDeletingProfile(profile)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={4} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Profiles;
